/**
 * Lucent Carat Lab - Custom Product Card Scripts
 * Handles:
 * - Slide carousel & image navigation (via explicit arrows/dots only, no hover swap)
 * - Clean swatch variant selection, variant image swapping, and price updates
 * - LocalStorage wishlist toggle
 * - Dynamic theme editor and AJAX collection re-renders
 */

(function () {
  'use strict';

  const WISHLIST_STORAGE_KEY = 'lucent_wishlist_items';

  function getWishlist() {
    try {
      const data = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(items) {
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('Could not save to localStorage', e);
    }
  }

  function initProductCard(card) {
    if (card.dataset.productCardInitialized) return;
    card.dataset.productCardInitialized = 'true';

    const track = card.querySelector('[data-card-track]');
    const slides = card.querySelectorAll('[data-slide]');
    const dots = card.querySelectorAll('[data-slide-dot]');
    const prevBtn = card.querySelector('[data-slide-prev]');
    const nextBtn = card.querySelector('[data-slide-next]');
    const swatchButtons = card.querySelectorAll('[data-swatch-button]');
    const swatchActiveLabel = card.querySelector('[data-swatch-active-label]');
    const priceEl = card.querySelector('[data-card-price]');
    const comparePriceEl = card.querySelector('[data-card-compare-price]');
    const cardLinks = card.querySelectorAll('[data-card-link]');
    const wishlistBtn = card.querySelector('[data-wishlist-button]');

    let currentSlide = 0;

    // --- Slide Switching with smooth horizontal slide ---
    function showSlide(index) {
      if (slides.length <= 1) return;
      const nextSlide = (index + slides.length) % slides.length;

      if (track) {
        track.style.transition = 'transform 0.38s cubic-bezier(0.25, 1, 0.5, 1)';
        track.style.transform = `translateX(-${nextSlide * 100}%)`;
      }

      slides.forEach((slide, idx) => {
        const isActive = idx === nextSlide;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === nextSlide);
      });

      // Synchronize swatch active state if slide corresponds to a metal
      swatchButtons.forEach((sBtn) => {
        const sIdx = sBtn.getAttribute('data-slide-index');
        if (sIdx !== null && parseInt(sIdx, 10) === nextSlide) {
          swatchButtons.forEach((b) => b.classList.remove('is-active'));
          sBtn.classList.add('is-active');
          const sName = sBtn.getAttribute('data-swatch-name');
          if (swatchActiveLabel && sName) {
            swatchActiveLabel.textContent = sName;
          }
          const vPrice = sBtn.getAttribute('data-variant-price');
          if (priceEl && vPrice) {
            priceEl.textContent = vPrice;
          }
        }
      });

      currentSlide = nextSlide;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        showSlide(currentSlide - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        showSlide(currentSlide + 1);
      });
    }

    dots.forEach((dot, dotIdx) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        showSlide(dotIdx);
      });
    });

    // Mobile Touch Swipe support with real-time sliding feel
    const mediaEl = card.querySelector('.premium-product-card__media');
    if (mediaEl && track && slides.length > 1) {
      let touchStartX = 0;
      let touchStartY = 0;
      let isDragging = false;
      let currentTranslate = 0;

      mediaEl.addEventListener('touchstart', (e) => {
        if (!e.touches || e.touches.length === 0) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        isDragging = true;
        currentTranslate = -currentSlide * 100;
        track.style.transition = 'none';
      }, { passive: true });

      mediaEl.addEventListener('touchmove', (e) => {
        if (!isDragging || !e.touches || e.touches.length === 0) return;
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = currentX - touchStartX;
        const diffY = currentY - touchStartY;

        // If swipe is primarily horizontal
        if (Math.abs(diffX) > Math.abs(diffY)) {
          const movePercent = (diffX / (mediaEl.offsetWidth || 300)) * 100;
          track.style.transform = `translateX(${currentTranslate + movePercent}%)`;
        }
      }, { passive: true });

      mediaEl.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        isDragging = false;
        track.style.transition = 'transform 0.38s cubic-bezier(0.25, 1, 0.5, 1)';
        const touchEndX = e.changedTouches && e.changedTouches.length > 0 ? e.changedTouches[0].clientX : touchStartX;
        const diffX = touchEndX - touchStartX;

        if (diffX < -35) {
          showSlide(currentSlide + 1); // Swipe left -> next slide
        } else if (diffX > 35) {
          showSlide(currentSlide - 1); // Swipe right -> prev slide
        } else {
          showSlide(currentSlide); // Return to current
        }
      }, { passive: true });
    }

    // NOTE: On hover, we intentionally do NOT change the image per design instructions.

    // --- Interactive Swatch Variant Selection ---
    swatchButtons.forEach((btn) => {
      function selectSwatch(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        // Set active swatch styling
        swatchButtons.forEach((s) => s.classList.remove('is-active'));
        btn.classList.add('is-active');

        const swatchName = btn.getAttribute('data-swatch-name');
        const variantUrl = btn.getAttribute('data-variant-url');
        const variantPrice = btn.getAttribute('data-variant-price');
        const variantComparePrice = btn.getAttribute('data-variant-compare-price');
        const variantImage = btn.getAttribute('data-variant-image');

        // Update swatch text label
        if (swatchActiveLabel && swatchName) {
          swatchActiveLabel.textContent = swatchName;
        }

        // Update Price
        if (priceEl && variantPrice) {
          priceEl.textContent = variantPrice;
        }

        if (comparePriceEl) {
          if (variantComparePrice && variantComparePrice.trim().length > 0) {
            comparePriceEl.textContent = variantComparePrice;
            comparePriceEl.classList.remove('is-hidden');
          } else {
            comparePriceEl.textContent = '';
            comparePriceEl.classList.add('is-hidden');
          }
        }

        // Update Card Links href
        if (variantUrl) {
          cardLinks.forEach((link) => {
            link.setAttribute('href', variantUrl);
          });
        }

        // Update Image directly on the slide without any overlapping elements
        const slideIndexAttr = btn.getAttribute('data-slide-index');
        if (slideIndexAttr !== null && slideIndexAttr !== '' && !isNaN(parseInt(slideIndexAttr, 10))) {
          const targetSlide = parseInt(slideIndexAttr, 10);
          if (slides[targetSlide]) {
            showSlide(targetSlide);
            if (variantImage && variantImage.trim().length > 0) {
              const targetImg = slides[targetSlide].querySelector('img');
              if (targetImg && targetImg.src !== variantImage) {
                targetImg.srcset = `${variantImage} 900w`;
                targetImg.src = variantImage;
              }
            }
            return;
          }
        }

        if (variantImage && variantImage.trim().length > 0 && slides.length > 0) {
          let matchingSlideIndex = -1;
          const varFilename = variantImage.split('/').pop().split('?')[0];

          slides.forEach((slide, idx) => {
            const img = slide.querySelector('img');
            if (img && img.src) {
              const slideFilename = img.src.split('/').pop().split('?')[0];
              if (varFilename === slideFilename || variantImage.includes(slideFilename) || img.src.includes(varFilename)) {
                matchingSlideIndex = idx;
              }
            }
          });

          if (matchingSlideIndex >= 0) {
            showSlide(matchingSlideIndex);
          } else {
            const activeSlide = slides[currentSlide] || slides[0];
            const activeImg = activeSlide ? activeSlide.querySelector('img') : null;
            if (activeImg) {
              activeImg.srcset = `${variantImage} 900w`;
              activeImg.src = variantImage;
            }
          }
        }
      }

      btn.addEventListener('click', selectSwatch);
    });

    // --- Wishlist Handler ---
    if (wishlistBtn) {
      const productId = wishlistBtn.getAttribute('data-wishlist-button') || wishlistBtn.getAttribute('data-product-id');

      if (window.LucentWishlist && window.LucentWishlist.has(productId)) {
        wishlistBtn.classList.add('is-active');
        wishlistBtn.setAttribute('aria-pressed', 'true');
      } else {
        const savedItems = getWishlist();
        if (savedItems.includes(productId)) {
          wishlistBtn.classList.add('is-active');
          wishlistBtn.setAttribute('aria-pressed', 'true');
        }
      }

      wishlistBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const productData = {
          id: productId,
          handle: wishlistBtn.getAttribute('data-product-handle') || card.getAttribute('data-product-handle') || '',
          title: wishlistBtn.getAttribute('data-product-title') || card.querySelector('.premium-product-card__title a')?.textContent?.trim() || '',
          url: wishlistBtn.getAttribute('data-product-url') || card.querySelector('.premium-product-card__title a')?.getAttribute('href') || '',
          image: wishlistBtn.getAttribute('data-product-image') || card.querySelector('.premium-product-card__image-main')?.getAttribute('src') || '',
          price: wishlistBtn.getAttribute('data-product-price') || card.querySelector('.price-item--regular, .price-item')?.textContent?.trim() || '',
          compare_at_price: wishlistBtn.getAttribute('data-product-compare-at-price') || card.querySelector('.price-item--compare')?.textContent?.trim() || '',
          variantId: wishlistBtn.getAttribute('data-product-variant-id') || card.querySelector('[data-selected-variant-id]')?.value || ''
        };

        if (window.LucentWishlist) {
          window.LucentWishlist.toggle(productData);
        } else {
          const currentWishlist = getWishlist();
          const index = currentWishlist.indexOf(productId);

          if (index > -1) {
            currentWishlist.splice(index, 1);
            wishlistBtn.classList.remove('is-active');
          } else {
            currentWishlist.push(productId);
            wishlistBtn.classList.add('is-active');
          }

          saveWishlist(currentWishlist);
        }
      });
    }
  }

  function initAllCards(root) {
    const context = root || document;
    context.querySelectorAll('[data-product-card]').forEach(initProductCard);
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllCards());
  } else {
    initAllCards();
  }

  // Shopify section render events (Theme editor and AJAX facets)
  document.addEventListener('shopify:section:load', (e) => {
    initAllCards(e.target);
  });

  // Observe dynamically added cards (facets / infinite scroll / filters)
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes && mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              if (node.matches && node.matches('[data-product-card]')) {
                initProductCard(node);
              } else if (node.querySelectorAll) {
                node.querySelectorAll('[data-product-card]').forEach(initProductCard);
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.LucentProductCard = {
    init: initAllCards
  };
})();